import { ViteReactSSG } from 'vite-react-ssg';

import { routes } from '@app/router/routes';
import { hydrateWhenRouterReady } from '@app/ssg/hydrate-when-router-ready';
import { initSentry } from '@/shared/observability/sentry';
import '@app/providers/i18n'; // funnel i18next bootstrap (side-effect)
import '@app/styles/global.css';
import '@/marketing/index.css'; // Tailwind + design tokens for the marketing site
import { initConsent, onConsentChange } from '@shared/consent';
import { consentModeUpdate, initTagManager, startEventConsentHandling } from '@shared/analytics';
import { startAttributionCapture } from '@shared/attribution';
import { initSupportChat } from '@shared/support-chat';

// Before anything else, so a failure during i18n/router setup is still reported.
// No-ops during the prerender pass and whenever VITE_SENTRY_DSN is unset.
initSentry();

/**
 * Stale-deploy recovery (client only). Each deploy replaces the hashed JS chunks
 * (`aws s3 sync --delete` purges the old ones), so a client still running an old
 * index.html can request a chunk that no longer exists. Paired with the
 * CloudFront fix that makes missing `/assets/*` return a real 404, Vite rejects
 * the dynamic import and fires `vite:preloadError`; we do ONE hard reload to pull
 * the fresh index.html + chunk manifest. A 10s cooldown (sessionStorage) breaks
 * reload loops if the reload doesn't resolve it, while still allowing recovery
 * from a later, independent redeploy in the same tab.
 */
/**
 * Each client-side start-up below is guarded on its own. They run at module top
 * level, so one that throws — a corrupted storage entry, a consent library that
 * fails to load — would otherwise take the whole entry down, and the site with it.
 * Measurement is never worth that.
 */
function guarded(name: string, start: () => void): void {
  try {
    start();
  } catch (error) {
    console.error(`[${name}] did not start`, error);
  }
}

if (typeof window !== 'undefined') {
  const LATCH = 'ditChunkReloadAt';
  window.addEventListener('vite:preloadError', (event) => {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(LATCH) || 0);
    if (now - last < 10_000) return; // just reloaded → don't loop
    sessionStorage.setItem(LATCH, String(now));
    event.preventDefault();
    window.location.reload();
  });

  /**
   * Measurement (DIT-513) — its consent listeners before the banner itself.
   *
   * Registered before `initConsent()` runs, so a stored answer the library
   * announces at start-up cannot be missed, however early inside its own `run()`
   * it announces it. An earlier version registered it one statement after and
   * argued that `run()` is asynchronous — true only as long as the library reaches
   * its first `await` before invoking the callback, which is not ours to rely on.
   * An update that arrives this early pushes the denied default first itself
   * (`consentModeUpdate`), so the default still precedes it.
   */
  guarded('consent mode', () => onConsentChange(consentModeUpdate));

  /**
   * The funnel's events go out whatever the banner's answer (Consent Mode
   * advanced); this keeps their stored once-only keys in step with it — written
   * on a grant, removed on a refusal — and is registered before the banner for the
   * same reason as the listener above.
   */
  guarded('event consent', startEventConsentHandling);

  /**
   * Where this visitor came from (DIT-513) — client only, and registered before the
   * banner for the same reason as the listeners above: the stored answer is
   * announced through `onConsentChange`, and a listener added after the banner
   * starts depends on the library's internal timing not to miss it.
   *
   * Runs on the first load rather than at the signup form, because that is where
   * the advertising parameters actually are: they arrive on `/nl/?gclid=…` and
   * are long gone by the time somebody reaches the funnel. Click identifiers
   * are kept only with marketing consent, campaign values with analytics, and a
   * visitor who declines both leaves nothing behind.
   *
   * Before the banner has started no category reads as granted, so the start-up
   * pass keeps nothing on its own: it notes the address and sweeps an expired
   * record, and the capture happens when the answer is announced — at start-up
   * for a returning visitor, on the answer for a first-time one, whose click is
   * the one the advertisement was paid for.
   */
  guarded('attribution', startAttributionCapture);

  /**
   * Cookie consent (DIT-402) — client only, and deliberately outside the React
   * tree. Every marketing route is prerendered, so a React banner would bake a
   * "not yet asked" state into the static HTML; the library renders its own DOM
   * after load instead. One init covers the marketing site and the funnel both.
   */
  guarded('consent', initConsent);

  /**
   * Then the container: `initTagManager()` pushes the denied-by-default state and
   * only then loads it, because a container that arrives first runs its tags once
   * with no consent state and Google reads a missing state as granted.
   */
  guarded('tag manager', initTagManager);

  /**
   * Support chat (DIT-336) — client only, and imperative for the same reason as
   * the banner above: prerendered routes have nothing to hydrate it against.
   * Anonymous by definition here; it no-ops where no inbox is configured.
   */
  void initSupportChat();
}

/**
 * Entry — vite-react-ssg (DIT-264 / S7). `vite-react-ssg build` prerenders the
 * marketing/blog routes to static HTML (the `includedRoutes` list in
 * vite.config.ts), while the signup funnel stays CSR (no HTML emitted, served by
 * the SPA shell). A plain `vite build` / `vite dev` uses the same entry as a
 * client SPA. Providers (MUI theme, emotion) live in the `RootProviders` route.
 *
 * The setup callback holds hydration until the router has finished its first
 * navigation. Without it a warm reload hydrated before vite-react-ssg's
 * route loaders had settled, React drew a second copy of the page after the
 * prerendered one, and the copy the visitor sees was dead — no header control
 * answered. The race and why waiting removes it: `hydrate-when-router-ready.ts`.
 */
export const createRoot = ViteReactSSG({ routes }, hydrateWhenRouterReady);
